"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * ツールチップの表示遅延時間などを設定するプロバイダーコンポーネント。
 * アプリケーションのルートレベル、またはツールチップを使用する特定のセクションでラップして使用します。
 * @param {object} props - コンポーネントのプロパティ。
 * @param {number} [props.delayDuration=0] - ツールチップが表示されるまでの遅延時間 (ミリ秒)。
 * @param {React.ReactNode} props.children - 子要素。
 * @returns {JSX.Element} `TooltipPrimitive.Provider`コンポーネント。
 */
function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

/**
 * ツールチップのルートコンポーネント。
 * `TooltipTrigger`と`TooltipContent`を子要素として含みます。
 * @param {object} props - `TooltipPrimitive.Root`コンポーネントのプロパティ。
 * @returns {JSX.Element} ツールチップのルート要素。
 */
function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

/**
 * ツールチップを表示するトリガーとなる要素をラップするコンポーネント。
 * 通常、ボタンやアイコンなどがトリガーとなります。
 * @param {object} props - `TooltipPrimitive.Trigger`コンポーネントのプロパティ。
 * @returns {JSX.Element} ツールチップトリガー要素。
 */
function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

/**
 * ツールチップとして表示されるコンテンツを定義するコンポーネント。
 *
 * @remarks
 * tooltip 内では説明文に `text-muted-foreground` がよく使われるが、
 * dark surface の上では通常の muted token だとコントラスト不足になりやすい。
 * そのため、tooltip 内に限って muted text を少し明るく補正し、
 * 既存 call site の class 指定を変えずに読みやすさを保つ。
 *
 * @param {object} props - コンポーネントのプロパティ。
 * @param {string} [props.className] - コンテンツ要素に適用する追加のCSSクラス。
 * @param {number} [props.sideOffset=4] - トリガー要素からのオフセット距離。
 * @param {React.ReactNode} props.children - ツールチップ内に表示するコンテンツ。
 * @returns {JSX.Element} ツールチップコンテンツ要素。
 */
function TooltipContent({
  className,
  sideOffset = 4,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "bg-slate-900 text-slate-50 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs  dark:bg-slate-800 dark:text-slate-100",
          "break-words leading-normal [&_.text-muted-foreground]:text-slate-200/90 dark:[&_.text-muted-foreground]:text-slate-200/90",
          className,
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
